import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { Link } from "react-router-dom";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import swal from "sweetalert";
import {
  setFilterField,
  setVisibleColumns,
} from "../../app/itemGridPreferencesSlice";
import {
  mergeVisibleColumns,
  needsPreferencesUpdate,
} from "../../utils/preferencesUtils";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";



const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const DAY_SHORT = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

const getDefaultDays = () =>
  DAYS_OF_WEEK.map((day) => ({
    day,
    checked: false,
    timeSlots: [{ from: "", to: "", description: "" }],
  }));

const emptyForm = () => ({
  id: null,
  name: "",
  description: "",
  days: getDefaultDays(),
});

const ServiceTime = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [servicePeriods, setServicePeriods] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const fetchSeqRef = useRef(0);

  const pageKey = "serviceTimes";
  const defaultVisibleColumns = {
    name: true,
    description: true,
    openDays: true,
  };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(
    preferences.visibleColumns,
    defaultVisibleColumns
  );
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const isEdit = Boolean(form?.id);

  const transformApiDataToSchedules = useCallback((apiData) => {
    return DAYS_OF_WEEK.map((dayName) => {
      const dayDetails = (apiData || []).filter((d) => d.dayName === dayName);
      return {
        day: dayName,
        checked: dayDetails.length > 0,
        timeSlots:
          dayDetails.length > 0
            ? dayDetails.map((d) => ({
                id: d.id,
                from: moment(d.openTime, "HH:mm").format("HH:mm"),
                to: moment(d.closeTime, "HH:mm").format("HH:mm"),
                description: d.description || "",
              }))
            : [{ from: "", to: "", description: "" }],
      };
    });
  }, []);

  const transformSchedulesToApiData = useCallback(
    (schedules, servicePeriodId) => {
      const details = [];
      schedules.forEach((day) => {
        if (day.checked && day.timeSlots?.length > 0) {
          day.timeSlots.forEach((slot) => {
            if (
              slot.from &&
              slot.to &&
              moment(slot.from, ["HH:mm", "h:mm A"]).isValid() &&
              moment(slot.to, ["HH:mm", "h:mm A"]).isValid()
            ) {
              details.push({
                id: slot.id || 0,
                compID: CompID,
                sT_ID: servicePeriodId,
                openTime: moment(slot.from, ["HH:mm", "h:mm A"]).format("HH:mm"),
                closeTime: moment(slot.to, ["HH:mm", "h:mm A"]).format("HH:mm"),
                dayName: day.day,
                dateTime: new Date().toISOString(),
              });
            }
          });
        }
      });
      return details;
    },
    [CompID]
  );

  const getOpenDaysLabel = (days = []) => {
    const open = days.filter((d) => d.checked);
    if (!open.length) return "No days set";
    if (open.length === 7) return "Every day";
    return open.map((d) => DAY_SHORT[d.day] || d.day).join(", ");
  };

  const fetchServiceTimes = useCallback(async () => {
    if (!CompID) return;
    const fetchSeq = ++fetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `${APIs.GET_ALL_SERVICE_TIME}?compID=${CompID}`
      );

      if (fetchSeq !== fetchSeqRef.current) return;

      if (response.data.status && response.data.data) {
        const periods = response.data.data.map((p) => ({
          id: p.id,
          name: p.sT_Name || "",
          description: p.sT_Description || "",
          days: transformApiDataToSchedules(p.detail || []),
        }));
        setServicePeriods(periods);
        if (!search.trim()) {
          applyCatalogTotalFromFetch(setCatalogTotalRecords, periods.length);
        }
      } else {
        setServicePeriods([]);
        showErrorToast(response.data.message || "Failed to load service times");
      }
    } catch (err) {
      if (fetchSeq !== fetchSeqRef.current) return;
      setServicePeriods([]);
      showErrorToast(
        err.response?.data?.message || "Failed to load service times"
      );
    } finally {
      if (fetchSeq === fetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, search, transformApiDataToSchedules]);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (
      needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)
    ) {
      dispatch(
        setVisibleColumns({
          pageKey,
          columns: mergeVisibleColumns(
            preferences.visibleColumns,
            defaultVisibleColumns
          ),
        })
      );
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(
        setFilterField({ pageKey, field: "pageSize", value: defaultPageSize })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useDebouncedEffect(() => fetchServiceTimes(), [fetchServiceTimes]);

  const filteredPeriods = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return servicePeriods;
    return servicePeriods.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
    );
  }, [servicePeriods, search]);

  const totalRecords = filteredPeriods.length;
  const totalPages = Math.max(
    1,
    Math.ceil(totalRecords / Number(pageSize.value))
  );
  const pagedPeriods = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return filteredPeriods.slice(start, start + size);
  }, [filteredPeriods, pageNumber, pageSize]);

  useResetPageOnFilterChange(setPageNumber, [search, pageSize?.value]);

  useInvalidateFetchOnFilterChange(fetchSeqRef, [search, pageSize?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) setPageNumber(totalPages);
  }, [pageNumber, totalPages]);

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch]
  );

  const openCreateModal = () => {
    setForm(emptyForm());
    setIsModalOpen(true);
  };

  const openEditModal = async (period) => {
    setForm({
      id: period.id,
      name: period.name || "",
      description: period.description || "",
      days: period.days?.length ? period.days : getDefaultDays(),
    });
    setIsModalOpen(true);

    if (!period.id) return;

    try {
      setScheduleLoading(true);
      const response = await axiosInstance.get(
        `${APIs.GET_SERVICE_TIME_BY_ID}?id=${period.id}`
      );
      if (response.data.status && response.data.data) {
        const data = response.data.data;
        setForm({
          id: data.id ?? period.id,
          name: data.sT_Name || period.name || "",
          description: data.sT_Description || period.description || "",
          days: transformApiDataToSchedules(data.detail || []),
        });
      }
    } catch {
      showErrorToast("Failed to load schedule details");
    } finally {
      setScheduleLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setForm(emptyForm());
    setScheduleLoading(false);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      showErrorToast("Please enter a name");
      return;
    }

    const hasValid = form.days.some(
      (d) =>
        d.checked &&
        d.timeSlots.some(
          (s) =>
            s.from &&
            s.to &&
            moment(s.from, ["HH:mm", "h:mm A"]).isValid() &&
            moment(s.to, ["HH:mm", "h:mm A"]).isValid()
        )
    );
    if (!hasValid) {
      showErrorToast("Add at least one day with valid time slots.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        id: form.id || 0,
        compID: CompID,
        sT_Name: form.name.trim(),
        sT_Description: form.description || "",
        sT_AddUpdateDate: new Date().toISOString(),
        detail: transformSchedulesToApiData(form.days, form.id || 0),
      };

      const response = form.id
        ? await axiosInstance.put(APIs.PUT_UPDATE_SERVICE_TIME, payload)
        : await axiosInstance.post(APIs.POST_ADD_SERVICE_TIME, payload);

      if (!response.data.status) {
        showErrorToast(response.data.message || "Failed to save");
        return;
      }

      showSuccessToast(
        form.id ? "Service time updated." : "Service time created."
      );
      closeModal();
      fetchServiceTimes();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (period) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Delete "${period.name || "this service time"}"?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmDelete) return;

    if (!period.id) {
      showSuccessToast("Removed.");
      fetchServiceTimes();
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.delete(
        `${APIs.DELETE_SERVICE_TIME}?id=${period.id}&CompID=${CompID}`
      );
      if (!response.data.status) {
        showErrorToast(response.data.message || "Failed to delete");
        return;
      }
      showSuccessToast("Service time deleted.");
      fetchServiceTimes();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (period) => {
    setForm({
      id: null,
      name: `${period.name || "Copy"} (Copy)`,
      description: period.description || "",
      days: (period.days || getDefaultDays()).map((d) => ({
        day: d.day,
        checked: d.checked,
        timeSlots: (d.timeSlots || []).map((s) => ({
          from: s.from,
          to: s.to,
          description: s.description || "",
        })),
      })),
    });
    setIsModalOpen(true);

    if (!period.id) return;

    try {
      setScheduleLoading(true);
      const response = await axiosInstance.get(
        `${APIs.GET_SERVICE_TIME_BY_ID}?id=${period.id}`
      );
      if (response.data.status && response.data.data?.detail) {
        setForm((prev) => ({
          ...prev,
          days: transformApiDataToSchedules(response.data.data.detail).map(
            (d) => ({
              ...d,
              timeSlots: d.timeSlots.map((s) => ({
                from: s.from,
                to: s.to,
                description: s.description || "",
              })),
            })
          ),
        }));
      }
    } catch {
      // keep list data already loaded into form
    } finally {
      setScheduleLoading(false);
    }
  };

  const hasFiltersApplied = () => search.trim() !== "";
  const clearFilters = () => setSearch("");

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No service times match "${search.trim()}". Try a different search or clear filters.`;
    }
    return "There are no service times yet. Create one to set your opening hours.";
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/tableManagement/serviceTime">Table Management</Link>
            </li>
            <li className="breadcrumb-item active">Service Hours</li>
          </ol>
          <h1 className="page-header mb-0">Service Hours</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={openCreateModal}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
            Add Service Time
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && (
          <GridSectionLoader loading minHeight="400px" />
        )}

        {showEmptyCatalogState && (
          <AddButton onClick={openCreateModal} title="Add Service Time" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Service Times"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name or description..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {!loading && filteredPeriods.length === 0 && (
                  <GridEmptyState
                    isDarkMode={isDarkMode}
                    themeColor={themeColor}
                    title="No service times found"
                    description={getEmptyStateDescription()}
                    onClearFilters={
                      hasFiltersApplied() ? clearFilters : undefined
                    }
                    clearFiltersLabel="Clear search"
                  />
                )}

                {!loading && pagedPeriods.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {visibleColumns.name && (
                            <th className="pt-2 pb-2">Name</th>
                          )}
                          {visibleColumns.description && (
                            <th className="pt-2 pb-2">Description</th>
                          )}
                          {visibleColumns.openDays && (
                            <th className="pt-2 pb-2">Open Days</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedPeriods.map((period) => (
                          <tr key={period.id || period.name}>
                            {visibleColumns.name && (
                              <td
                                className="align-middle"
                                style={{ cursor: "pointer" }}
                                onClick={() => openEditModal(period)}
                              >
                                {period.name || "—"}
                              </td>
                            )}
                            {visibleColumns.description && (
                              <td
                                className="align-middle"
                                style={{ cursor: "pointer" }}
                                onClick={() => openEditModal(period)}
                              >
                                {period.description || "—"}
                              </td>
                            )}
                            {visibleColumns.openDays && (
                              <td
                                className="align-middle"
                                style={{ cursor: "pointer" }}
                                onClick={() => openEditModal(period)}
                              >
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: `${themeColor}22`,
                                    color: themeColor,
                                    fontWeight: 600,
                                  }}
                                >
                                  {getOpenDaysLabel(period.days)}
                                </span>
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <TableActionsMenu>
                                <TableActionsItem
                                  icon="fa fa-edit"
                                  onClick={() => openEditModal(period)}
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  icon="fa fa-copy"
                                  onClick={() => handleDuplicate(period)}
                                >
                                  Duplicate
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => handleDelete(period)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <CustomModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={isEdit ? "Update Service Time" : "Add Service Time"}
        subtitle="Set the name and weekly opening hours"
        fontSize="18px"
        fontWeight={400}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText={isEdit ? "Update" : "Save"}
        submitButtonDisabled={saving || scheduleLoading || !form.name?.trim()}
        onSubmit={handleSave}
        modalSize="modal-xl"
        minHeight="auto"
        maxHeight="90vh"
      >
        <GridSectionLoader loading={saving || scheduleLoading} minHeight="240px">
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <FormInput
                label="Name"
                placeHolder="e.g. Breakfast, Lunch"
                type="text"
                value={form.name}
                onchange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            <div className="col-md-6">
              <FormInput
                label="Description (optional)"
                placeHolder="Short description"
                type="text"
                value={form.description}
                onchange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <h6
            className="mb-2"
            style={{ color: isDarkMode ? "#ffffff" : undefined }}
          >
            Weekly schedule
          </h6>
          <div
            className="rounded-3 p-3"
            style={{
              backgroundColor: isDarkMode ? "#1e2428" : "#f8fafc",
              border: isDarkMode ? "1px solid #404040" : "1px solid #e2e8f0",
            }}
          >
            <ScheduleManager
              schedules={Array.isArray(form.days) ? form.days : []}
              setSchedules={(daysOrUpdater) =>
                setForm((prev) => {
                  const current = Array.isArray(prev.days) ? prev.days : [];
                  const next =
                    typeof daysOrUpdater === "function"
                      ? daysOrUpdater(current)
                      : daysOrUpdater;
                  return {
                    ...prev,
                    days: Array.isArray(next) ? next : current,
                  };
                })
              }
              isProfilePage={false}
              isDarkMode={isDarkMode}
            />
          </div>
        </GridSectionLoader>
      </CustomModal>
    </div>
  );
};

export default ServiceTime;
